"use client";

import { useGoogleLogin } from "@react-oauth/google";
import { Form, Formik } from "formik";
import Link from "next/link";
import { FC, useState } from "react";
import { appleAuthHelpers, useScript } from "react-apple-signin-auth";
import { object, string } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { env } from "@/env";
import { Button, Divider, Input, Typography } from "@/shared/ui";
import AppleIcon from "@/svgs/apple.svg";
import EyeIcon from "@/svgs/eye.svg";
import GoogleIcon from "@/svgs/google.svg";
import { useUIStore } from "stores/ui.store";
import { useUserStore } from "stores/user.store";

import { useSignupWithApple } from "./hooks/use-signup-with-apple.hook";
import { useSignupWithCreds } from "./hooks/use-signup-with-creds.hook";
import { useSignupWithGoogle } from "./hooks/use-signup-with-google.hook";
import styles from "./signup-form.module.scss";
import { SignupFormScheme } from "./signup-form.types";

const validationScheme = object({
  email: string().email("Wrong email format").min(1, "Field is required"),
  password: string().min(8, "Must be at least 8 characters"),
  confirmedPassword: string().min(1, "Field is required"),
}).refine(({ password, confirmedPassword }) => password === confirmedPassword, {
  message: "The confirmation password doesn't match",
  path: ["confirmedPassword"],
});

const SignupForm: FC = () => {
  useScript(appleAuthHelpers.APPLE_SCRIPT_SRC);

  const { mutate: mutateCreds, isPending: isSignupWithCredsPending } = useSignupWithCreds();
  const { mutate: mutateGoogle, isPending: isSignupWithGooglePending } = useSignupWithGoogle();
  const { mutate: mutateApple, isPending: isSignupWithApplePending } = useSignupWithApple();

  const [isPasswordHidden, setIsPasswordHidden] = useState<boolean>(true);
  const [isConfirmedPasswordHidden, setIsConfirmedPasswordHidden] = useState<boolean>(true);

  const role = useUserStore((state) => state.role);
  const openToast = useUIStore((state) => state.actions.toast.openToast);

  const google = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      mutateGoogle({ authorizationCode: code, role });
    },
    onError: () => {
      openToast({ variant: "error", message: "Error: GoogleProvider" });
    },
  });

  const signupWithGoogleHandler = async () => {
    if (!role) {
      return openToast({ variant: "error", message: "Your role wasn't provided!" });
    }

    google();
  };
  const signupWithAppleHandler = () => {
    if (!role) {
      return openToast({ variant: "error", message: "Your role wasn't provided!" });
    }

    appleAuthHelpers.signIn({
      authOptions: {
        clientId: env.NEXT_PUBLIC_APPLE_CLIENT_ID,
        redirectURI: env.NEXT_PUBLIC_FRONTEND_URL,
        usePopup: true,
        scope: "email name",
      },
      onSuccess: (data: { authorization: { code: string } }) => {
        mutateApple({ authorizationCode: data.authorization.code, role });
      },
      onError: (e: any) => {
        console.error(e);
      },
    });
  };
  const onSubmitWithCredentials = ({ email, password }: SignupFormScheme) => {
    if (!role) {
      return openToast({ variant: "error", message: "Your role wasn't provided!" });
    }

    mutateCreds({ email, password, role });
  };

  return (
    <Formik<SignupFormScheme>
      validateOnBlur={false}
      validateOnChange={false}
      initialValues={{ role: "muse", email: "", password: "", confirmedPassword: "" }}
      validationSchema={toFormikValidationSchema(validationScheme)}
      onSubmit={onSubmitWithCredentials}
    >
      {({ values, errors, dirty, isValid, validateField, setFieldValue }) => (
        <Form className={styles.form}>
          <div className={styles.inputs}>
            <Input
              required
              placeholder="Email"
              value={values.email}
              message={errors.email}
              variant={errors.email ? "error" : undefined}
              onBlur={() => validateField("email")}
              onValueChange={async (value) => {
                await setFieldValue("email", value);
                errors.email && (await validateField("email"));
              }}
            />
            <Input
              required
              placeholder="Password"
              value={values.password}
              message={errors.password ?? "Must be at least 8 characters"}
              variant={errors.password ? "error" : undefined}
              type={isPasswordHidden ? "password" : "text"}
              endContent={
                <EyeIcon className={styles.passwordEyeIcon} onClick={() => setIsPasswordHidden((prev) => !prev)} />
              }
              onBlur={() => validateField("password")}
              onValueChange={async (value) => {
                await setFieldValue("password", value);
                await validateField("password");
              }}
            />
            <Input
              required
              placeholder="Retype your password"
              value={values.confirmedPassword}
              message={errors.confirmedPassword}
              variant={errors.confirmedPassword ? "error" : undefined}
              type={isConfirmedPasswordHidden ? "password" : "text"}
              endContent={
                <EyeIcon
                  className={styles.passwordEyeIcon}
                  onClick={() => setIsConfirmedPasswordHidden((prev) => !prev)}
                />
              }
              onBlur={() => validateField("confirmedPassword")}
              onValueChange={async (value) => {
                await setFieldValue("confirmedPassword", value);
                await validateField("confirmedPassword");
              }}
            />
            <Button
              fullWidth
              size="big"
              type="submit"
              disabled={
                isSignupWithApplePending ||
                isSignupWithGooglePending ||
                isSignupWithCredsPending ||
                !dirty ||
                !isValid ||
                Object.values(values).some((v) => !v)
              }
            >
              {isSignupWithApplePending || isSignupWithGooglePending || isSignupWithCredsPending
                ? "Loading..."
                : "Sign Up"}
            </Button>
          </div>

          <Divider text="Or" />

          <Button size="big" variant="outlined" icon={GoogleIcon} type="button" onClick={signupWithGoogleHandler}>
            Continue with Google
          </Button>
          <Button size="big" variant="outlined" icon={AppleIcon} type="button" onClick={signupWithAppleHandler}>
            Continue with Apple
          </Button>
          <Typography variant="footnote" align="center" className={styles.hintToSignup}>
            Have you been here before?{" "}
            <Link className={styles.signupLink} href={"/auth/login"}>
              Login
            </Link>
          </Typography>
        </Form>
      )}
    </Formik>
  );
};

export { SignupForm };
